import { useEffect, useState } from "react";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../components/ui/select";
import { ChevronDown, ChevronUp, Plus, Save } from "lucide-react";
import { Field } from "./shared";
import { bankAccountTypeLabels, suggestBankLedgerName } from "./constants";

/**
 * Shared Bank Account create/edit form, used by both the inline "Add" panel
 * on the Bank Accounts list and the dedicated /bank-accounts/:id/edit page,
 * so the two flows can never drift apart.
 *
 * Phase 5 correction: the user is creating a PHYSICAL bank account here, not
 * configuring an accounting-ledger relationship. The normal flow never asks
 * them to pick from existing ledgers - that is exactly how two unrelated
 * bank accounts could end up sharing "Main bank" and merging balances.
 * Instead a dedicated ledger name is proposed automatically (e.g. "HDFC
 * Bank - 7890") and the user may edit it before saving; the backend creates
 * that ledger itself. Linking to an EXISTING ledger is only reachable via
 * the "Advanced: Link existing accounting ledger" toggle (only offered on
 * create, when `allowAdvancedLink` is set) - for migration/import scenarios
 * only. Once a bank account exists, its Linked Ledger can never be changed
 * from this form at all (see the disabled field in edit mode below).
 */
export default function BankAccountForm({ form, setForm, availableLedgers, busy, onSubmit, onCancel, submitLabel, isEdit, allowAdvancedLink = false, openingBalanceLocked = false }) {
  const [ledgerNameTouched, setLedgerNameTouched] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    if (isEdit || ledgerNameTouched || showAdvanced) return;
    const suggested = suggestBankLedgerName(form.bankName, form.branch, form.accountNumber);
    setForm((f) => (f.ledgerName === suggested ? f : { ...f, ledgerName: suggested }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.bankName, form.branch, form.accountNumber, isEdit, ledgerNameTouched, showAdvanced]);

  return (
    <div>
      <div className="grid gap-3 md:grid-cols-4">
        <Field label="Bank name">
          <Input value={form.bankName} onChange={(e) => setForm((f) => ({ ...f, bankName: e.target.value }))} placeholder="HDFC Bank" />
        </Field>
        <Field label="Branch">
          <Input value={form.branch} onChange={(e) => setForm((f) => ({ ...f, branch: e.target.value }))} placeholder="Andheri East" />
        </Field>
        <Field label="Account name">
          <Input value={form.accountName} onChange={(e) => setForm((f) => ({ ...f, accountName: e.target.value }))} placeholder="Sunrise CHS Ltd" />
        </Field>
        <Field label={isEdit ? "Account number (leave blank to keep)" : "Account number"}>
          <Input value={form.accountNumber} onChange={(e) => setForm((f) => ({ ...f, accountNumber: e.target.value }))} placeholder="XXXXXXXXXXXX" />
        </Field>
        <Field label="IFSC">
          <Input value={form.ifsc} onChange={(e) => setForm((f) => ({ ...f, ifsc: e.target.value.toUpperCase() }))} placeholder="HDFC0000123" />
        </Field>
        <Field label="Account type">
          <Select value={form.accountType} onValueChange={(v) => setForm((f) => ({ ...f, accountType: v }))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(bankAccountTypeLabels).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>

        {!isEdit && !showAdvanced && (
          <Field label="Ledger name">
            <Input
              value={form.ledgerName}
              onChange={(e) => { setLedgerNameTouched(true); setForm((f) => ({ ...f, ledgerName: e.target.value })); }}
              placeholder="e.g. HDFC Bank - 7890"
            />
            <p className="mt-1 text-xs text-[#6B7280]">A dedicated accounting ledger with this name will be created automatically for this account.</p>
          </Field>
        )}

        {isEdit && (
          <Field label="Linked ledger">
            <Input value={form.linkedLedgerName || ""} disabled className="bg-[#F3F4F6] text-[#6B7280]" />
            <p className="mt-1 text-xs text-[#6B7280]">The linked accounting ledger cannot be changed from this form.</p>
          </Field>
        )}

        <div className="flex flex-col justify-end gap-1.5 text-xs text-[#374151]">
          <label className="flex items-center gap-2">
            <input type="checkbox" className="h-3.5 w-3.5 rounded-sm border-[#D1D5DB]" checked={form.isPrimaryCollectionAccount} onChange={(e) => setForm((f) => ({ ...f, isPrimaryCollectionAccount: e.target.checked }))} />
            Is primary collection account
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" className="h-3.5 w-3.5 rounded-sm border-[#D1D5DB]" checked={form.reconciliationEnabled} onChange={(e) => setForm((f) => ({ ...f, reconciliationEnabled: e.target.checked }))} />
            Reconciliation enabled
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" className="h-3.5 w-3.5 rounded-sm border-[#D1D5DB]" checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} />
            Active
          </label>
        </div>
      </div>

      <div className="mt-4 rounded-sm border border-[#E5E7EB] bg-[#F9FAFB] p-3">
        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#6B7280]">Opening Balance</p>
        <div className="grid gap-3 md:grid-cols-3">
          <Field label="Amount">
            <Input
              type="number" min="0" step="0.01" disabled={openingBalanceLocked}
              value={form.openingBalance} onChange={(e) => setForm((f) => ({ ...f, openingBalance: e.target.value }))}
              placeholder="0.00"
            />
          </Field>
          <Field label="Balance is">
            <Select value={form.openingBalanceSide} onValueChange={(v) => setForm((f) => ({ ...f, openingBalanceSide: v }))} disabled={openingBalanceLocked}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="debit">Dr (positive balance)</SelectItem>
                <SelectItem value="credit">Cr (overdraft)</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="As of date">
            <Input type="date" disabled={openingBalanceLocked} value={form.openingAsOfDate} onChange={(e) => setForm((f) => ({ ...f, openingAsOfDate: e.target.value }))} />
          </Field>
        </div>
        <p className="mt-2 text-xs text-[#6B7280]">
          {openingBalanceLocked
            ? "This account already has posted transactions, so its opening balance can no longer be changed here. Post a dated Journal Entry instead to make a correction."
            : "What this account already held as of the date above. Saved immediately with the account - no separate posting step."}
        </p>
      </div>

      {!isEdit && allowAdvancedLink && (
        <div className="mt-4 border-t border-[#E5E7EB] pt-3">
          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            className="flex items-center gap-1 text-xs font-semibold text-[#6B7280] hover:text-[#111827]"
          >
            {showAdvanced ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            Advanced: Link existing accounting ledger
          </button>
          {showAdvanced && (
            <div className="mt-2 rounded-sm border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs text-amber-800">
                For migrating accounting data from another system only. Linking this physical bank account to a ledger
                that already carries activity can merge its balance and history with this account. Normal Society users
                should leave the Ledger Name field above instead.
              </p>
              <div className="mt-2">
                <Field label="Existing Bank-group ledger">
                  <Select
                    value={form.linkedLedgerId}
                    onValueChange={(v) => setForm((f) => ({ ...f, linkedLedgerId: v }))}
                  >
                    <SelectTrigger><SelectValue placeholder="Select a Bank-group ledger" /></SelectTrigger>
                    <SelectContent>
                      {availableLedgers.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="mt-3 flex gap-2">
        <Button disabled={busy} onClick={onSubmit} className="bg-[#111827] text-white hover:bg-[#374151]">
          {isEdit ? <><Save className="mr-2 h-4 w-4" /> {submitLabel || "Save changes"}</> : <><Plus className="mr-2 h-4 w-4" /> {submitLabel || "Add bank account"}</>}
        </Button>
        {onCancel && <Button type="button" variant="outline" disabled={busy} onClick={onCancel}>Cancel</Button>}
      </div>
    </div>
  );
}
